/* eslint-disable @typescript-eslint/no-explicit-any */
import { LeaderboardItem } from "@models/LeaderboardItem";
import { PokerRunEvent } from "@models/PokerRunEvent";
/* eslint-disable indent */
/* eslint-disable class-methods-use-this */
// eslint-disable-next-line object-curly-newline
import {
  getFirestore,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  doc,
  collection,
  collectionGroup,
  query,
  orderBy,
  startAt,
  endAt,
  GeoPoint,
  where,
  DocumentData,
  DocumentSnapshot,
  onSnapshot,
  Timestamp,
  Unsubscribe,
  arrayRemove,
  updateDoc,
  arrayUnion,
  limit,
} from "firebase/firestore";
import * as geofire from "geofire-common";
import { Geopoint } from "geofire-common";
import Config from "@/config/Config";
import { getFunctions, httpsCallable } from "firebase/functions";
import { EventManager } from "@models/EventManager";
import useDate from "@/modules/useDate";
import RegistrationDataService from "./RegistrationDataService";

const { formatISODate } = useDate();

class PokerRunEventDataService {

  collectionName = "events";

  async getLeaderboard(
    eventId: string,
    participantIds: string[],
    handleLeaderboardChange: (updatedLeaderboard: {
      positions: LeaderboardItem[];
      createTimestamp: Timestamp;
    }) => void
  ) {
    const db = getFirestore();
    const eventDoc = doc(db, this.collectionName, eventId);

    const LeaderboardRef = collection(eventDoc, "leaderboard");
    const LeaderboardQuery = query(LeaderboardRef, orderBy("position"));
    const TopLeaderboardQuery = query(LeaderboardRef, orderBy("position"), limit(50));
    const docs = await getDocs(TopLeaderboardQuery);
    const positions: LeaderboardItem[] = [];
    docs.forEach((d) => {
      positions.push(d.data() as LeaderboardItem);
    });

    if (participantIds && participantIds.length > 0) {
      const UserLeaderboardQuery = query(
        LeaderboardRef,
        where("participantId", "in", participantIds),
        orderBy("position")
      );
      const userDocs = await getDocs(UserLeaderboardQuery);
      userDocs.forEach((d) => {
        if (!positions.some((x) => x.participantId === d.data().participantId)) {
          positions.push(d.data() as LeaderboardItem);
        }
      });
    }

    let unsubscribe = null;
    if (handleLeaderboardChange) {
      unsubscribe = onSnapshot(LeaderboardQuery, (leaderboardDocs) => {
        const updatedPositions: LeaderboardItem[] = [];
        leaderboardDocs.forEach((ud) => {
          updatedPositions.push(ud.data() as LeaderboardItem);
        });
        handleLeaderboardChange({ positions: updatedPositions, createTimestamp: Timestamp.now() });
      });
    }

    return {
      leaderboard: { positions, createTimestamp: Timestamp.now() },
      unsubscribe,
    };
  }

  async getAll(): Promise<PokerRunEvent[]> {
    const db = getFirestore();
    const data = await getDocs(collection(db, this.collectionName));
    const results: PokerRunEvent[] = [];
    data.forEach((item) => {
      const eventData = item.data() as PokerRunEvent;
      if (eventData && eventData.name) {
        results.push({ id: item.id, ...item.data() } as PokerRunEvent);
      }
    });
    return results;
  }

  async getByEventIds(eventIds: string[]): Promise<PokerRunEvent[]> {
    try {
      if (eventIds.length === 0) {
        return [];
      }

      const db = getFirestore();
      const eventCollection = collection(db, this.collectionName);
      const results: PokerRunEvent[] = [];

      // Split eventIds into batches of 30
      const batches = [];
      for (let i = 0; i < eventIds.length; i += 30) {
        batches.push(eventIds.slice(i, i + 30));
      }

      // Execute queries for each batch
      for (const batch of batches) {
        const q = query(eventCollection, where("id", "in", batch));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          snapshot.docs.forEach((item) => {
            results.push({ id: item.id, ...item.data() } as PokerRunEvent);
          });
        }
      }

      return results;
    } catch (error) {
      console.error("Error getting documents: ", error);
      throw error;
    }
  }

  async getHostedPokerRuns(userId: string, ownerOnly: boolean): Promise<PokerRunEvent[]> {
    const db = getFirestore();
    const readonlyAdminData = [];
    const adminData = [];
    const assistantData = [];
    const pokerRunEvents = query(
      collection(db, this.collectionName),
      // eslint-disable-next-line comma-dangle
      where("organizerId", "==", `${userId}`)
    );

    if (!ownerOnly) {
      const adminPokerRunEvents = query(
        collection(db, this.collectionName),
        // eslint-disable-next-line comma-dangle
        where("admins", "array-contains", `${userId}`)
      );

      const readonlyAdminPokerRunEvents = query(
        collection(db, this.collectionName),
        // eslint-disable-next-line comma-dangle
        where("readonlyAdmins", "array-contains", `${userId}`)
      );

      const assistantPokerRunEvents = query(
        collection(db, this.collectionName),
        // eslint-disable-next-line comma-dangle
        where("assistants", "array-contains", `${userId}`)
      );

      readonlyAdminData.push(...(await getDocs(readonlyAdminPokerRunEvents)).docs);
      adminData.push(...(await getDocs(adminPokerRunEvents)).docs);
      assistantData.push(...(await getDocs(assistantPokerRunEvents)).docs);
    }

    const data = await getDocs(pokerRunEvents);
    const allData = [...data.docs, ...readonlyAdminData, ...adminData, ...assistantData];
    const results: PokerRunEvent[] = [];

    allData.forEach((item) => {
      if (results.find((x) => x.id === item.id) === undefined) {
        results.push({ ...item.data(), id: item.id } as PokerRunEvent);
      }
    });
    return results;
  }

  async getUserPokerRuns(userId: string): Promise<PokerRunEvent[]> {
    const registrations = await RegistrationDataService.getByUser(userId);
    const eventIds = registrations.map((item) => item.eventId);
    // distinct eventIds
    const distinctEventIds = [...new Set(eventIds)];
    return await this.getByEventIds(distinctEventIds);
  }

  async get(id: string): Promise<PokerRunEvent> {
    const db = getFirestore();
    const docRef = doc(collection(db, this.collectionName), id);
    const data = await getDoc(docRef);
    return {
      ...data.data(),
      id: data.id,
    } as PokerRunEvent;
  }

  async getSubscription(
    id: string,
    handleEventParticipantChange: (pokerRunEvent: PokerRunEvent) => void
  ): Promise<Unsubscribe | null> {
    const db = getFirestore();

    const eventDoc = doc(db, this.collectionName, id);

    const unsubscribe = onSnapshot(
      eventDoc,
      (snapshot) => {
        handleEventParticipantChange({ ...snapshot.data(), id: snapshot.id } as PokerRunEvent);
      },
      (error) => {
        console.error("Error:", error);
      }
    );

    return unsubscribe;
  }

  async save(event: PokerRunEvent): Promise<PokerRunEvent> {
    const db = getFirestore();
    const tmpEvent = { ...event } as any;

    if (tmpEvent._internal) {
      delete tmpEvent._internal;
    }

    let docRef;
    if (event.id) {
      docRef = doc(collection(db, this.collectionName), event.id);
      await setDoc(docRef, tmpEvent as Partial<unknown>, { merge: true });
    } else {
      docRef = await addDoc(collection(db, this.collectionName), tmpEvent);
    }
    return this.get(docRef.id);
  }

  async duplicateEvent(id: any, userId: string) {
    const pokerRunEvent = await this.get(id);
    if(!pokerRunEvent) return null;
    if(pokerRunEvent.organizerId !== userId) return null;
    
    delete pokerRunEvent.id;
    delete pokerRunEvent._internal;
    pokerRunEvent.name = pokerRunEvent.name + " - Copy";
    pokerRunEvent.status = "setup";
    pokerRunEvent.eventDate = formatISODate(new Date());
    const newEvent = await this.save(pokerRunEvent);
    return newEvent.id;
  }

  async search(coor: GeoPoint, distince: number): Promise<Array<PokerRunEvent>> {
    const db = getFirestore();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIso = formatISODate(today);
    const MILES_TO_METERS = 1609.34;
    const center: Geopoint = [coor.latitude, coor.longitude];
    const radiusInMiles = distince;
    const radiusInM = radiusInMiles * MILES_TO_METERS;
    const bounds = geofire.geohashQueryBounds(center, radiusInM);
    const promises = [];
    for (const b of bounds) {
      const q = query(
        collection(db, this.collectionName),
        where("eventDate", ">=", todayIso),
        orderBy("geoHash"),
        startAt(b[0]),
        endAt(b[1])
      );
      promises.push(getDocs(q));
    }

    // Collect all the query results together into a single list
    const snapshots = await Promise.all(promises);

    const matchingDocs: Array<PokerRunEvent> = [];
    for (const snap of snapshots) {
      for (const item of snap.docs) {
        const lat = item.get("lat");
        const lng = item.get("long");
        const eventDate = item.get("eventDate");

        // We have to filter out a few false positives due to GeoHash
        // accuracy, but most will match
        const distanceInKm = geofire.distanceBetween([lat, lng], center);
        const distanceInM = distanceInKm * 1000;
        if (distanceInM <= radiusInM && eventDate >= todayIso && item.get("status") !== "setup") {
          matchingDocs.push({
            id: item.id,
            ...item.data(),
          } as unknown as PokerRunEvent);
        }
      }
    }

    return matchingDocs;
  }

  async setAdmins(eventId: string, managerIds: string[]) {
    const db = getFirestore();
    const docRef = doc(collection(db, this.collectionName), eventId);
    await updateDoc(docRef, {
      admins: [...managerIds],
    });
    return this.get(docRef.id);
  }

  async setAssistants(eventId: string, managerIds: string[]) {
    const db = getFirestore();
    const docRef = doc(collection(db, this.collectionName), eventId);
    await updateDoc(docRef, {
      assistants: [...managerIds],
    });
    return this.get(docRef.id);
  }
}

export default new PokerRunEventDataService();
